import React, { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react'
import { AlertCircle, Info } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * The one validation pattern (UX-102 / UX-130):
 *
 *   1. Each field shows its own error under it, in words, linked with
 *      aria-describedby and aria-invalid — <FormField>.
 *   2. A button that can't be pressed yet says why, right under it —
 *      <DisabledReason>.
 *   3. On submit with problems, a summary at the top lists them as links
 *      that jump to each field — <FormErrorSummary>.
 *
 *   <FormErrorSummary errors={{ email: 'Enter an email like name@example.com' }} labels={{ email: 'Email' }} />
 *   <div className="form-row">
 *     <FormField name="firstName" label="First name" error={errors.firstName} required>
 *       <input className="input-field" value={…} onChange={…} />
 *     </FormField>
 *     <FormField label="Phone" helper="We text job updates here.">
 *       <input className="input-field" type="tel" … />
 *     </FormField>
 *   </div>
 *   <button className="btn-primary" disabled={!canSave} aria-describedby="save-why">Save</button>
 *   {!canSave && <DisabledReason id="save-why">Add a customer name first.</DisabledReason>}
 *
 * FormField wires the id/label/aria onto its single child, so any input,
 * select, textarea or custom control that forwards those props works.
 */

export interface FormFieldProps {
  label: ReactNode
  /** The control. Receives id, aria-describedby, aria-invalid, aria-required. */
  children: ReactElement
  /** Field-level error in words: "Enter a phone number with 10 digits". */
  error?: string | null
  /** Hint shown when there's no error. */
  helper?: ReactNode
  required?: boolean
  /** Form field name; sets the id FormErrorSummary links to. */
  name?: string
  /** Override the generated id. */
  id?: string
  /** Visually hide the label (still read by screen readers). */
  hideLabel?: boolean
  className?: string
}

export function fieldDomId(name: string): string {
  return `field-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}`
}

export function FormField({ label, children, error, helper, required, name, id, hideLabel, className }: FormFieldProps) {
  const auto = useId()
  const childProps = (isValidElement(children) ? (children.props as Record<string, unknown>) : {}) ?? {}
  const fieldId = id ?? (name ? fieldDomId(name) : undefined) ?? (childProps.id as string | undefined) ?? `f${auto.replace(/:/g, '')}`
  const errorId = error ? `${fieldId}-error` : undefined
  const helperId = helper && !error ? `${fieldId}-helper` : undefined
  const describedBy = [childProps['aria-describedby'] as string | undefined, errorId, helperId].filter(Boolean).join(' ') || undefined

  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
        required: (childProps.required as boolean | undefined) ?? required,
      })
    : children

  return (
    <div className={cn('min-w-0 space-y-1.5', className)} data-field-invalid={error ? '' : undefined}>
      <label htmlFor={fieldId} className={cn('block text-sm font-medium', hideLabel && 'sr-only')} style={{ color: 'var(--midnight-text2)' }}>
        {label}
        {required && <span aria-hidden="true" className="ml-1" style={{ color: 'var(--midnight-text3)' }}>*</span>}
      </label>
      {control}
      {error ? (
        <p id={errorId} className="flex items-start gap-1.5 text-sm" style={{ color: 'var(--blush, var(--blush))' }}>
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : helper ? (
        <p id={helperId} className="text-sm" style={{ color: 'var(--midnight-text3)' }}>{helper}</p>
      ) : null}
    </div>
  )
}

/** The reason a button is disabled, shown right under it. Link it with aria-describedby. */
export function DisabledReason({ id, children, className }: { id?: string; children: ReactNode; className?: string }) {
  return (
    <p id={id} className={cn('mt-2 flex items-start gap-1.5 text-[13px]', className)} style={{ color: 'var(--midnight-text3)' }}>
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  )
}

export interface FormErrorSummaryProps {
  /** field name → message. Empty/undefined entries are skipped. */
  errors: Record<string, string | null | undefined>
  /** field name → label shown in the list. Defaults to the message alone. */
  labels?: Record<string, string>
  /** field name → DOM id when not the FormField default. */
  ids?: Record<string, string>
  title?: string
  className?: string
}

/**
 * Shown after a failed submit. Focus it (it is focusable and announced) so
 * keyboard and screen-reader users hear what to fix; each item jumps to its
 * field.
 */
export const FormErrorSummary = React.forwardRef<HTMLDivElement, FormErrorSummaryProps>(function FormErrorSummary(
  { errors, labels = {}, ids = {}, title, className },
  ref,
) {
  const entries = Object.entries(errors).filter(([, m]) => !!m) as [string, string][]
  if (entries.length === 0) return null
  const heading = title ?? (entries.length === 1 ? 'Fix 1 thing to continue' : `Fix ${entries.length} things to continue`)
  const jump = (name: string) => (e: React.MouseEvent) => {
    const el = document.getElementById(ids[name] ?? fieldDomId(name))
    if (!el) return
    e.preventDefault()
    el.focus()
    el.scrollIntoView?.({ block: 'center' })
  }
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      data-testid="form-error-summary"
      className={cn('rounded-2xl px-4 py-3 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-white', className)}
      style={{ background: 'var(--midnight-surface)', border: '0.5px solid rgb(var(--blush-rgb) / 0.45)', color: 'var(--midnight-text)' }}
    >
      <p className="flex items-center gap-2 font-semibold">
        <AlertCircle className="h-4 w-4 shrink-0" style={{ color: 'var(--blush, var(--blush))' }} aria-hidden="true" />
        {heading}
      </p>
      <ul className="mt-1.5 space-y-1 pl-6">
        {entries.map(([name, msg]) => (
          <li key={name}>
            <a href={`#${ids[name] ?? fieldDomId(name)}`} onClick={jump(name)} className="underline underline-offset-2" style={{ color: 'var(--midnight-text2)' }}>
              {labels[name] ? `${labels[name]}: ${msg}` : msg}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
})
